import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Alert,
  Button,
  Form,
  Input,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Upload,
} from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import useTable from '@/hooks/useTable';
import { fetchAuthType, fetchIsUploadSecretKey } from '@/slices/kopPermissionsSlice';
import { deleteRole, getRoles, uploadKeyFile } from '@/services/kopPermissionsService';
import ShadowCard from '@/components/ShadowCard';
import PermWrapper from '@/components/PermWrapper';
import { renderDateTime } from '@/utils/common';
import DisableBuriedPermWrapper from '@/components/DisableWrapper/DisableBuriedPermWrapper';
import DisableWrapper from '@/components/DisableWrapper';
import NoAuthAlert from '../../components/NoAuthAlert';
import Secret from '../../components/Secret';
import Modal from './Modal';

const { FormItem } = Form;

function Users({ fetchAuthType, info, fetchIsUploadSecretKey, isUploadSecretKey }) {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);
  const [update, setUpdate] = useState(false);

  // 查询初始化
  const initialData = useMemo(
    () => ({
      kafkaUser: searchParams.get('kafkaUser'),
    }),
    [searchParams],
  );

  const columns = [
    {
      colKey: 'kafkaUser',
      title: 'KopUser',
      width: 120,
    },
    {
      colKey: 'roleType',
      title: '认证方式',
      width: 150,
    },
    {
      colKey: 'roleKey',
      title: 'Password',
      cell: ({ row }) => <Secret>{row.roleKey}</Secret>,
    },
    {
      colKey: 'createByPlatform',
      title: '平台创建',
      cell: ({ row }) => (row.createByPlatform ? '是' : '否'),
      width: 90,
    },
    {
      colKey: 'createTime',
      title: '创建时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'updateTime',
      title: '更新时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'note',
      title: '备注',
      width: 240,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='kop:auth:user:edit'>
            <Link theme='primary' onClick={() => doUpdate(row)} hover='color'>
              编辑
            </Link>
          </PermWrapper>
          <Popconfirm theme='danger' content='确定删除？' onConfirm={() => handleDel(row)}>
            <DisableBuriedPermWrapper resource='kop:auth:user:delete'>
              <Link theme='danger' hover='color'>
                移除
              </Link>
            </DisableBuriedPermWrapper>
          </Popconfirm>
        </Space>
      ),
      width: 120,
    },
  ];

  // 修改
  const doUpdate = (row) => {
    setUpdate({
      clusterName,
      kafkaUser: row.kafkaUser,
      type: row.roleType,
      note: row.note,
    });
    setVisible(true);
  };

  // 删除
  const handleDel = async (row) => {
    await deleteRole({ clusterName, kafkaUser: row.kafkaUser });
    doRefresh();
  };
  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      formData.append('clusterName', clusterName);
      formData.append('createBy', info.username || '');
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await uploadKeyFile(formData);
        MessagePlugin.success('上传成功！');
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
        fetchIsUploadSecretKey(clusterName);
      }
    }
  };

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getRoles({ ...params, clusterName });
    setData(data);
  });

  const { header, config, setData, doRefresh, doSearch, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <FormItem name='kafkaUser'>
        <Input placeholder='请输入KopUser' clearable />
      </FormItem>
    ),
    operators: (
      <>
        {!isUploadSecretKey && (
          <DisableBuriedPermWrapper resource='kop:auth:user:upload'>
            <Upload theme='file' onSelectChange={doUpload}>
              <Button variant='outline'>上传秘钥</Button>
            </Upload>
          </DisableBuriedPermWrapper>
        )}
        <PermWrapper resource='kop:auth:user:create'>
          <Button
            theme='primary'
            onClick={() => {
              setVisible(true);
              setUpdate(false);
            }}
          >
            创建KopUser
          </Button>
        </PermWrapper>
      </>
    ),
  });

  const doInit = useMemoizedFn(async () => {
    fetchAuthType();
    fetchIsUploadSecretKey(clusterName);

    // 有初始化条件
    if (searchParams.size > 1) {
      await doSearch(initialData);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [doInit, clusterName]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      // 刷新
      doRefresh();
    }
  };

  return (
    <Root>
      <PermWrapper resource='kop:auth:user:upload'>
        {isUploadSecretKey && (
          <Alert
            style={{ margin: '6px 0 14px' }}
            theme='info'
            message='您已上传秘钥，重新上传将会覆盖已有数据，请谨慎操作'
            operation={
              <DisableWrapper>
                <Upload theme='file' onSelectChange={doUpload}>
                  <Link theme='primary' hover='color'>
                    重新上传
                  </Link>
                </Upload>
              </DisableWrapper>
            }
          />
        )}
      </PermWrapper>
      <NoAuthAlert name='KopUser' />
      <Body>
        {header}
        <Table {...config} />
        <Modal
          clusterName={clusterName}
          visible={visible}
          update={update}
          handleClose={handleClose}
        />
      </Body>
    </Root>
  );
}

export default connect(
  (state) => ({
    info: state.auth.info,
    isUploadSecretKey: state.kopPermissions.isUploadSecretKey,
  }),
  {
    fetchAuthType,
    fetchIsUploadSecretKey,
  },
)(Users);

const Root = styled.div`
  padding-right: 20px;
`;

const Body = styled(ShadowCard)`
  .t-form-inline .t-form__item {
    margin-right: var(--td-comp-margin-s);
  }
`;
